import { createServerFn } from "@tanstack/react-start";

type CachedRates = {
  spotUsdPerOz: number;
  pkrPerUsd: number;
  inrPerUsd: number;
  fetchedAt: string;
};

// Worker-runtime safe in-memory cache. Cold starts will re-fetch; that's fine.
let cache: CachedRates | null = null;
const ONE_HOUR_MS = 10 * 60 * 1000;
const FETCH_TIMEOUT_MS = 3500;

// Race any fetch against a hard timeout so SSR never stalls on a slow upstream.
async function fetchWithTimeout(url: string, timeoutMs = FETCH_TIMEOUT_MS): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}

async function fetchFresh(): Promise<CachedRates> {
  const spotUsdPerOz = await fetchSpotUsdPerOz();
  const fx = await fetchFx();
  return {
    spotUsdPerOz,
    pkrPerUsd: fx.PKR,
    inrPerUsd: fx.INR,
    fetchedAt: new Date().toISOString(),
  };
}

// Live XAU/USD spot. Primary: gold-api.com. Fallback: Yahoo Finance XAUUSD=X.
// Sanity-check the result against a plausible band so a bad upstream never
// poisons the cache with an obviously wrong price.
async function fetchSpotUsdPerOz(): Promise<number> {
  const isPlausible = (p: unknown): p is number =>
    typeof p === "number" && Number.isFinite(p) && p > 1500 && p < 10000;

  try {
    const r = await fetchWithTimeout("https://api.gold-api.com/price/XAU");
    if (r.ok) {
      const d = (await r.json()) as { price?: number };
      if (isPlausible(d?.price)) return d.price as number;
    }
  } catch {
    // fall through
  }

  try {
    const r = await fetchWithTimeout(
      "https://query1.finance.yahoo.com/v8/finance/chart/XAUUSD=X?interval=1m&range=1d",
    );
    if (r.ok) {
      const d = (await r.json()) as {
        chart?: { result?: Array<{ meta?: { regularMarketPrice?: number } }> };
      };
      const p = d?.chart?.result?.[0]?.meta?.regularMarketPrice;
      if (isPlausible(p)) return p as number;
    }
  } catch {
    // fall through
  }

  throw new Error("No live gold source available");
}

// Primary FX source: fawazahmed0 currency-api (mid-market, matches Google).
// Fallback: open.er-api.com (daily averages — runs ~1% high for PKR).
export async function fetchFx(): Promise<{ PKR: number; INR: number }> {
  try {
    const r = await fetchWithTimeout(
      "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json",
    );
    if (r.ok) {
      const d = (await r.json()) as { usd: Record<string, number> };
      if (d?.usd?.pkr && d?.usd?.inr) {
        return { PKR: d.usd.pkr, INR: d.usd.inr };
      }
    }
  } catch {
    // fall through to backup
  }
  try {
    const r2 = await fetchWithTimeout(
      "https://latest.currency-api.pages.dev/v1/currencies/usd.json",
    );
    if (r2.ok) {
      const d = (await r2.json()) as { usd: Record<string, number> };
      if (d?.usd?.pkr && d?.usd?.inr) {
        return { PKR: d.usd.pkr, INR: d.usd.inr };
      }
    }
  } catch {
    // fall through
  }
  const r3 = await fetchWithTimeout("https://open.er-api.com/v6/latest/USD");
  if (!r3.ok) throw new Error("Failed to fetch FX");
  const fx = (await r3.json()) as { rates: Record<string, number> };
  return { PKR: fx.rates.PKR, INR: fx.rates.INR };
}

export const getGoldRates = createServerFn({ method: "GET" }).handler(async () => {
  const now = Date.now();
  if (cache && now - new Date(cache.fetchedAt).getTime() < ONE_HOUR_MS) {
    return cache;
  }
  // Hard cap across the full fresh-fetch chain so SSR never stalls.
  try {
    const fresh = await Promise.race<CachedRates>([
      fetchFresh(),
      new Promise<CachedRates>((_, reject) =>
        setTimeout(() => reject(new Error("gold ssr timeout")), FETCH_TIMEOUT_MS + 500),
      ),
    ]);
    cache = fresh;
    return cache;
  } catch {
    // Prefer stale cache over a hard-coded baseline so we never publish a
    // wildly stale spot price. Only fall back to a conservative anchor if
    // we have literally no data (cold start + every upstream down).
    if (cache) return cache;
    return {
      spotUsdPerOz: 4156,
      pkrPerUsd: 278,
      inrPerUsd: 83.5,
      fetchedAt: new Date().toISOString(),
    } satisfies CachedRates;
  }
});